import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { priceFormatterV3 } from "../../settings/themeSettings";

// Fonction pour détecter si une valeur est un pourcentage
const isPercentage = (value) => {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return text.includes('%');
};

// Fonction pour obtenir le style d'une cellule selon le type de données
const getCellStyle = (value, baseStyle, percentageStyle) => {
    return isPercentage(value) ? percentageStyle : baseStyle;
};

// Styles pour le PDF inspirés du style Excel du dashboard
const styles = StyleSheet.create({
    container: {
        width: '100%',
        height: '100%',
        padding: 20,
        fontFamily: 'Helvetica',
        backgroundColor: '#ffffff'
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#000000',
        textAlign: 'center',
        marginBottom: 25,
        padding: 20,
        backgroundColor: '#f5f5f5',
        borderRadius: 8,
        textTransform: 'uppercase',
        letterSpacing: 1
    },
    tableContainer: {
        width: '100%',
        border: '2px solid #000000',
        borderRadius: 6,
        overflow: 'hidden'
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#000000',
        color: 'white',
        minHeight: 45
    },
    headerCell: {
        flex: 1,
        padding: 15,
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'center',
        color: 'white',
        borderRight: '1px solid #ffffff',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        alignSelf: 'center'
    },
    headerCellLast: {
        flex: 1,
        padding: 15,
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'center',
        color: 'white',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        alignSelf: 'center'
    },
    tableRow: {
        flexDirection: 'row',
        borderBottom: '1px solid #000000',
        minHeight: 40,
        backgroundColor: '#ffffff'
    },
    tableRowEven: {
        flexDirection: 'row',
        borderBottom: '1px solid #000000',
        backgroundColor: '#f8f9fa',
        minHeight: 40
    },
    tableCell: {
        flex: 1,
        padding: 12,
        fontSize: 11,
        borderRight: '1px solid #000000',
        alignSelf: 'center',
        color: '#000000'
    },
    tableCellLast: {
        flex: 1,
        padding: 12,
        fontSize: 11,
        alignSelf: 'center',
        color: '#000000'
    },
    tableCellRight: {
        flex: 1,
        padding: 12,
        fontSize: 11,
        borderRight: '1px solid #ecf0f1',
        textAlign: 'right',
        fontFamily: 'Helvetica-Bold',
        alignSelf: 'center',
        color: '#2c3e50'
    },
    tableCellRightLast: {
        flex: 1,
        padding: 12,
        fontSize: 11,
        textAlign: 'right',
        fontFamily: 'Helvetica-Bold',
        alignSelf: 'center',
        color: '#2c3e50'
    },
    // Styles spécifiques pour les pourcentages
    tableCellPercentage: {
        flex: 1,
        padding: 12,
        fontSize: 11,
        borderRight: '1px solid #ecf0f1',
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
        alignSelf: 'center',
        color: '#2c3e50',
        backgroundColor: 'transparent' // Fond transparent pour les pourcentages
    },
    tableCellPercentageLast: {
        flex: 1,
        padding: 12,
        fontSize: 11,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
        alignSelf: 'center',
        color: '#2c3e50',
        backgroundColor: 'transparent' // Fond transparent pour les pourcentages
    },
    totalRow: {
        flexDirection: 'row',
        backgroundColor: '#000000',
        minHeight: 45,
        borderTop: '2px solid #000000'
    },
    totalCell: {
        flex: 1,
        padding: 15,
        fontSize: 12,
        fontWeight: 'bold',
        color: 'white',
        borderRight: '1px solid #ffffff',
        alignSelf: 'center',
        textTransform: 'uppercase'
    },
    totalCellLast: {
        flex: 1,
        padding: 15,
        fontSize: 12,
        fontWeight: 'bold',
        color: 'white',
        alignSelf: 'center'
    },
    totalCellRight: {
        flex: 1,
        padding: 15,
        fontSize: 12,
        fontWeight: 'bold',
        color: 'white',
        borderRight: '1px solid #ffffff',
        textAlign: 'right',
        alignSelf: 'center'
    },
    totalCellRightLast: {
        flex: 1,
        padding: 15,
        fontSize: 12,
        fontWeight: 'bold',
        color: 'white',
        textAlign: 'right',
        alignSelf: 'center'
    },
    // Styles spécifiques pour les pourcentages dans les totaux
    totalCellPercentage: {
        flex: 1,
        padding: 15,
        fontSize: 12,
        fontWeight: 'bold',
        color: 'white',
        borderRight: '1px solid #ffffff',
        textAlign: 'center',
        alignSelf: 'center',
        backgroundColor: 'transparent'
    },
    footer: {
        marginTop: 'auto',
        paddingTop: 20,
        textAlign: 'center',
        fontSize: 9,
        color: '#7f8c8d',
        borderTop: '1px solid #ecf0f1',
        paddingVertical: 10
    },
    watermark: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) rotate(-45deg)',
        fontSize: 80,
        color: 'rgba(52, 73, 94, 0.05)',
        fontWeight: 'bold',
        zIndex: -1
    }
});

const DashboardTablePDF = ({ caption, data, title }) => {
    // Fonction pour déterminer si une ligne est un total
    const isTotalRow = (item) => {
        return item.title?.includes('TOTAL') || 
               item.title?.includes('Total') || 
               item.metrique?.includes('Total') ||
               item.metrique?.includes('TOTAL');
    };

    return (
        <View style={styles.container}>
            {/* Titre principal */}
            <Text style={styles.title}>
                {caption}
            </Text>

            {/* Tableau */}
            <View style={styles.tableContainer}>
                {/* En-tête du tableau */}
                {title && (
                    <View style={styles.tableHeader}>
                        {title.map((tl, index) => (
                            <Text 
                                key={index} 
                                style={index === title.length - 1 ? styles.headerCellLast : styles.headerCell}
                            >
                                {tl}
                            </Text>
                        ))}
                    </View>
                )}

                {/* Lignes de données */}
                {data.map((item, idx) => {
                    const isTotal = isTotalRow(item);
                    const isEven = idx % 2 === 0;
                    
                    if (isTotal) {
                        // Ligne de total avec style spécial
                        return (
                            <View key={idx} style={styles.totalRow}>
                                <Text style={styles.totalCell}>
                                    {item.title || item.metrique}
                                </Text>
                                {Array.isArray(item.value) ? 
                                    item.value.map((v, key) => {
                                        const isPct = isPercentage(v);
                                        const cellStyle = isPct ? styles.totalCellPercentage : styles.totalCellRight;
                                        return (
                                            <Text key={key} style={cellStyle}>
                                                {isNaN(Number(v)) ? v : priceFormatterV3(v)}
                                            </Text>
                                        );
                                    }) : 
                                    <Text style={styles.totalCellRight}>
                                        {isNaN(Number(item.value || item.montant)) ? 
                                            (item.value || item.montant) : 
                                            priceFormatterV3(item.value || item.montant)}
                                    </Text>
                                }
                                {item.pourcentage && (
                                    <Text style={getCellStyle(item.pourcentage, styles.totalCellRight, styles.totalCellPercentage)}>
                                        {item.pourcentage}
                                    </Text>
                                )}
                                {item.marge !== null && item.marge !== undefined && (
                                    <Text style={getCellStyle(item.marge, styles.totalCellRightLast, styles.tableCellPercentageLast)}>
                                        {isNaN(Number(item.marge)) ? item.marge : priceFormatterV3(item.marge)}
                                    </Text>
                                )}
                            </View>
                        );
                    } else {
                        // Ligne normale
                        return (
                            <View key={idx} style={isEven ? styles.tableRow : styles.tableRowEven}>
                                <Text style={styles.tableCell}>
                                    {item.title || item.metrique}
                                </Text>
                                {Array.isArray(item.value) ? 
                                    item.value.map((v, key) => {
                                        const isPct = isPercentage(v);
                                        const isLast = key === item.value.length - 1;
                                        let cellStyle;
                                        
                                        if (isPct) {
                                            cellStyle = isLast ? styles.tableCellPercentageLast : styles.tableCellPercentage;
                                        } else {
                                            cellStyle = isLast ? styles.tableCellRightLast : styles.tableCellRight;
                                        }
                                        
                                        return (
                                            <Text key={key} style={cellStyle}>
                                                {isNaN(Number(v)) ? v : priceFormatterV3(v)}
                                            </Text>
                                        );
                                    }) : 
                                    <Text style={styles.tableCellRight}>
                                        {isNaN(Number(item.value || item.montant)) ? 
                                            (item.value || item.montant) : 
                                            priceFormatterV3(item.value || item.montant)}
                                    </Text>
                                }
                                {item.pourcentage && (
                                    <Text style={getCellStyle(item.pourcentage, styles.tableCellRight, styles.tableCellPercentage)}>
                                        {item.pourcentage}
                                    </Text>
                                )}
                                {item.marge !== null && item.marge !== undefined && (
                                    <Text style={getCellStyle(item.marge, styles.tableCellRightLast, styles.tableCellPercentageLast)}>
                                        {isNaN(Number(item.marge)) ? item.marge : priceFormatterV3(item.marge)}
                                    </Text>
                                )}
                            </View>
                        );
                    }
                })}
            </View>

            {/* Pied de page */}
            <Text style={styles.footer}>
                Généré le {new Date().toLocaleDateString('fr-FR')} - TRAVEL MANAGER AFT
            </Text>
        </View>
    );
};

export default DashboardTablePDF;